import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { forumApi } from '../../api/forum';
import { filesApi } from '../../api/files';
import { UserContext } from '../../context/UserContext';
import { getMutedErrorMessage } from '../../utils/moderationErrors';
import { renderMarkdownLite } from '../../utils/markdownLite';
import MarkdownToolbar from './MarkdownToolbar';
import HrnbButton from '../controls/HrnbButton';
import HrnbForm from '../controls/HrnbForm';
import HrnbInput from '../controls/HrnbInput';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import FileUploader from './FileUploader';
import TagInput from './TagInput';
import '../../css/components/CreatePostForm.css'
import HrnbModal from "../controls/HrnbModal";

const DRAFT_KEY = 'hrnb_create_post_draft';

const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 10000;

export default function CreatePostForm({ isOpen, onClose, onSuccess, categories = [] }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const [draftRestored, setDraftRestored] = useState(false);
    const [draftError, setDraftError] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        categoryId: '',
        tags: [],
    });
    const [authorProfileId, setAuthorProfileId] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [previewMode, setPreviewMode] = useState(false);

    const contentTextareaRef = useRef(null);

    const autoSaveTimerRef = useRef(null);
    const formDataRef = useRef(formData);
    const uploadedFilesRef = useRef(uploadedFiles);

    // Синхронизируем refs с состоянием
    useEffect(() => { formDataRef.current = formData; }, [formData]);
    useEffect(() => { uploadedFilesRef.current = uploadedFiles; }, [uploadedFiles]);

    // Восстановление черновика при открытии
    useEffect(() => {
        if (!isOpen) return;

        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const draft = JSON.parse(saved);
                const hasContent = draft.title || draft.content || draft.categoryId || (draft.tags && draft.tags.length > 0);
                if (hasContent) {
                    setFormData({
                        title: draft.title || '',
                        content: draft.content || '',
                        categoryId: draft.categoryId || '',
                        tags: draft.tags || [],
                    });
                    setDraftRestored(true);

                    // Автоочистка уведомления через 5 сек
                    setTimeout(() => setDraftRestored(false), 5000);
                }
            }
        } catch (e) { /* ignore */ }
    }, [isOpen]);

    // Автосохранение каждые 5 секунд
    useEffect(() => {
        if (!isOpen) return;

        autoSaveTimerRef.current = setInterval(() => {
            const data = formDataRef.current;
            const hasContent = data.title || data.content || data.categoryId || (data.tags && data.tags.length > 0);
            if (!hasContent) return;

            try {
                const serialized = JSON.stringify({
                    title: data.title,
                    content: data.content,
                    categoryId: data.categoryId,
                    tags: data.tags,
                    savedAt: Date.now(),
                });

                // Проверяем размер: localStorage лимит ~5MB, оставляем запас
                if (serialized.length > 4_500_000) {
                    throw new Error('limit');
                }

                localStorage.setItem(DRAFT_KEY, serialized);
                setDraftSaved(true);
                setDraftError(null);
                // Убираем индикатор через 2 сек
                setTimeout(() => setDraftSaved(false), 2000);
            } catch (e) {
                console.warn('[CreatePostForm] Draft save failed:', e.message);
                setDraftSaved(false);
                setDraftError('Не удалось сохранить черновик: текст слишком большой');
            }
        }, 5000);

        return () => {
            if (autoSaveTimerRef.current) {
                clearInterval(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (!currentUser) return;
        const pid = currentUser.profiles?.[0]?.id;
        if (pid) {
            setAuthorProfileId(pid);
        }
    }, [currentUser]);

    // Если категорий нет, используем заглушки
    const displayedCategories = categories.length > 0 ? categories : [
        { id: 1, name: 'Обсуждения' },
        { id: 2, name: 'Вопросы и ответы' },
        { id: 3, name: 'Новости' },
        { id: 4, name: 'Предложения' }
    ];

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        if (!formData.title || !formData.title.trim()) {
            setError('Название поста обязательно');
            return false;
        }
        if (formData.title.trim().length < 3) {
            setError('Название поста должно быть не менее 3 символов');
            return false;
        }
        if (formData.title.length > MAX_TITLE_LENGTH) {
            setError(`Название поста не должно превышать ${MAX_TITLE_LENGTH} символов`);
            return false;
        }
        if (!formData.content || !formData.content.trim()) {
            setError('Содержимое поста обязательно');
            return false;
        }
        if (formData.content.trim().length < 10) {
            setError('Содержимое поста должно быть не менее 10 символов');
            return false;
        }
        if (formData.content.length > MAX_CONTENT_LENGTH) {
            setError(`Содержимое поста не должно превышать ${MAX_CONTENT_LENGTH} символов`);
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!currentUser) {
            setError('Необходимо авторизоваться для создания поста');
            return;
        }
        if (!authorProfileId) {
            setError('Не удалось определить профиль автора. Попробуйте позже.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const attachmentIds = uploadedFiles.map(f => f.id);

            const payload = {
                title: formData.title.trim(),
                content: formData.content.trim(),
                categoryId: formData.categoryId,
                authorId: authorProfileId,
                tags: formData.tags || [],
                attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
            };

            const result = await forumApi.createPost(payload);

            // Очищаем черновик сразу после успешного создания, до onSuccess/navigate
            try {
                localStorage.removeItem(DRAFT_KEY);
            } catch (e) { /* ignore */ }

            setSuccess(true);

            setTimeout(() => {
                const responseData = result.data || result;
                const postId = responseData?.id || responseData?.postId || result.id || result.postId;

                if (onSuccess) {
                    onSuccess(responseData);
                }
                handleClose();
                if (postId) {
                    try {
                        sessionStorage.setItem('hrnb_forum_from_forum', 'true');
                    } catch (e) { /* ignore */ }
                    navigate(`/forum/posts/${postId}`);
                }
            }, 1000);
        } catch (err) {
            console.error('[CreatePostForm] Failed to create post:', err);

            const mutedMsg = getMutedErrorMessage(err);
            if (mutedMsg) {
                setError(mutedMsg);
            } else if (err.response?.status === 500) {
                setError('Ошибка сервера. Обратитесь к администратору или попробуйте позже.');
            } else {
                const errorData = err.response?.data;
                const errorMessage = errorData?.message ||
                                    errorData?.error ||
                                    err.response?.statusText ||
                                    err.message ||
                                    'Не удалось создать пост. Попробуйте позже.';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return;
        setFormData({
            title: '',
            content: '',
            categoryId: '',
            tags: [],
        });
        setUploadedFiles([]);
        setError(null);
        setSuccess(false);
        setDraftSaved(false);
        setDraftRestored(false);
        setDraftError(null);
        try {
            localStorage.removeItem(DRAFT_KEY);
        } catch (e) { /* ignore */ }
        if (onClose) {
            onClose();
        }
    };

    const handleFileUploaded = (fileData) => {
        setUploadedFiles(prev => [...prev, fileData]);
    };

    const handleFileRemoved = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    if (!isOpen) return null;

    return (
        <HrnbModal open={isOpen} onClose={handleClose} title="Создание нового поста" size="large">
            <div className="create-post-form">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {success && (
                    <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                        Пост успешно создан!
                    </HrnbAlert>
                )}

                <HrnbForm onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Название *</label>
                            <HrnbInput
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder="Введите название поста"
                                disabled={loading}
                                maxLength={MAX_TITLE_LENGTH}
                            />
                            <span className={`form-char-counter ${formData.title.trim().length >= 3 ? 'form-char-counter--ok' : ''} ${formData.title.length > MAX_TITLE_LENGTH ? 'form-char-counter--error' : formData.title.length > MAX_TITLE_LENGTH - 20 ? 'form-char-counter--warn' : ''}`}>
                                {formData.title.length} / мин. 3 / макс. {MAX_TITLE_LENGTH}
                            </span>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Категория *</label>
                            <HrnbSelect
                                value={formData.categoryId}
                                onChange={(value) => handleChange('categoryId', value)}
                                options={displayedCategories.map(cat => ({
                                    value: cat.id.toString(),
                                    label: cat.name
                                }))}
                                placeholder="Выберите категорию"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <div className="form-content-header">
                                <label className="form-control-label">Содержимое *</label>
                                <HrnbButton
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPreviewMode(prev => !prev)}
                                    disabled={loading}
                                >
                                    {previewMode ? '✏️ Редактировать' : '👁️ Предпросмотр'}
                                </HrnbButton>
                            </div>
                            {previewMode ? (
                                <div className="create-post-preview">
                                    {formData.content.trim()
                                        ? renderMarkdownLite(formData.content)
                                        : <span className="create-post-preview-empty">Введите содержимое поста для предпросмотра</span>}
                                </div>
                            ) : (
                                <>
                                    <MarkdownToolbar
                                        textareaRef={contentTextareaRef}
                                        value={formData.content}
                                        onChange={(val) => handleChange('content', val)}
                                        disabled={loading}
                                    />
                                    <HrnbTextArea
                                        ref={contentTextareaRef}
                                        value={formData.content}
                                        onChange={(e) => handleChange('content', e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                                if (!loading) handleSubmit(e);
                                            }
                                        }}
                                        placeholder="Напишите содержимое поста..."
                                        rows={8}
                                        disabled={loading}
                                        maxLength={MAX_CONTENT_LENGTH}
                                    />
                                </>
                            )}
                            <span className={`form-char-counter ${formData.content.trim().length >= 10 ? 'form-char-counter--ok' : ''} ${formData.content.length > MAX_CONTENT_LENGTH ? 'form-char-counter--error' : formData.content.length > MAX_CONTENT_LENGTH - 500 ? 'form-char-counter--warn' : ''}`}>
                                {formData.content.length} / мин. 10 / макс. {MAX_CONTENT_LENGTH}
                            </span>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Теги</label>
                            <TagInput
                                value={formData.tags}
                                onChange={(newTags) => handleChange('tags', newTags)}
                                disabled={loading}
                                placeholder="Начните вводить тег..."
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Вложения (изображения, аудио, видео, документы)</label>
                            <FileUploader
                                onFileUploaded={handleFileUploaded}
                                onFileRemoved={handleFileRemoved}
                                uploadedFiles={uploadedFiles}
                                metadata="forum_attachment"
                                maxFileSize={30}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <div className="form-draft-indicator">
                            {draftSaved && <span className="form-draft-saved">💾 Черновик сохранён</span>}
                            {draftRestored && <span className="form-draft-restored">📄 Черновик восстановлен</span>}
                            {draftError && <span className="form-draft-error" title={draftError}>⚠️ {draftError}</span>}
                        </div>
                        <div className="form-actions-buttons">
                            <HrnbButton variant="outline" onClick={handleClose} disabled={loading}>
                                Отмена
                            </HrnbButton>
                            <HrnbButton type="submit" variant="primary" disabled={loading}>
                                {loading ? <HrnbLoader size="sm" /> : 'Создать пост'}
                            </HrnbButton>
                        </div>
                    </div>
                </HrnbForm>
            </div>
        </HrnbModal>
    );
}
